import { checkRemoteSignResponse, REMOTE_SIGN_FUNCTION_PATH, REMOTE_SIGN_PROTOCOL_VERSION, type RemoteSignErrorCode, type RemoteSignRequest, type RemoteSignResponse } from '../../supabase/functions/_shared/remoteSignProtocol';
import { devWebVersionWanted } from '../app/webVersion';
import { devRemoteSignTransport } from './devRemoteSign';

// Client transport to sign-v2 (remote signing of the agreement, Owner items 4 / 45). The inspector's actions
// carry the signed-in user's token; the client's page (open / sign) carries none — the link's token is the key.
export type RemoteSignCallOutcome = { kind: 'ok'; response: Extract<RemoteSignResponse, { ok: true }> } | { kind: 'failed'; code: RemoteSignErrorCode | 'offline' | 'network' };

export interface RemoteSignTransport {
  call(request: DistributiveOmit<RemoteSignRequest, 'protocolVersion'>, token: string | null): Promise<RemoteSignCallOutcome>;
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

function endpoint(): { url: string; anon: string } | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  return base.length > 0 && anon.length > 0 ? { url: `${base}${REMOTE_SIGN_FUNCTION_PATH}`, anon } : null;
}

export const fetchRemoteSignTransport: RemoteSignTransport = {
  async call(request, token) {
    const target = endpoint();
    if (!target) return { kind: 'failed', code: 'notConfigured' };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'failed', code: 'offline' };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30_000);
    try {
      const res = await fetch(target.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: target.anon, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ protocolVersion: REMOTE_SIGN_PROTOCOL_VERSION, ...request }),
        signal: controller.signal
      });
      const parsed = checkRemoteSignResponse(await res.json().catch(() => null));
      // Not our protocol at all (a platform 404 when the function is not deployed, an HTML error page).
      if (!parsed) return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : 'internalError' };
      return parsed.ok ? { kind: 'ok', response: parsed } : { kind: 'failed', code: parsed.code };
    } catch {
      return { kind: 'failed', code: 'network' };
    } finally {
      clearTimeout(timer);
    }
  }
};

let override: RemoteSignTransport | null = null;

export function setRemoteSignTransportForTests(transport: RemoteSignTransport | null): void {
  override = transport;
}

export function getRemoteSignTransport(): RemoteSignTransport {
  if (override) return override;
  // DEVELOPMENT ONLY: with the web-version switch the dev server walks remote signing against an in-browser
  // stand-in of sign-v2 (src/backend/devRemoteSign.ts). Never in a build.
  if (devWebVersionWanted() || (import.meta.env.DEV && typeof window !== 'undefined' && window.location.pathname.startsWith('/sign/'))) return devRemoteSignTransport;
  return fetchRemoteSignTransport;
}

// Where the link opens: the web version's address (VITE_WEB_APP_URL, e.g. https://app.nezha2capital.com).
// Without it this build cannot make links, and says so.
export function remoteSignBase(): string | null {
  const base = ((import.meta.env.VITE_WEB_APP_URL as string | undefined) ?? '').trim();
  if (base.length > 0) return base;
  // The development server makes links to itself, so the page can be tried on the same machine.
  return import.meta.env.DEV && typeof window !== 'undefined' ? window.location.origin : null;
}
